import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import {
  Clock,
  CheckCircle,
  Users,
  Award,
  Palette,
  Video,
  Layers,
  Camera,
  Sparkles,
  Quote,
  User,
} from 'lucide-react';
import PageHero from '../components/PageHero';
import imageSrc from '../assets/image.jpeg';
const image = imageSrc;

// ─── Custom hook ───
const useVisibility = (threshold = 0.2) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, isVisible];
};

// ─── Animation variants ───
const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay } },
});

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 },
  },
};

const About = () => {
  const stats = [
    { value: 5, label: 'Years Experience', icon: Clock, suffix: '+' },
    { value: 120, label: 'Projects Delivered', icon: CheckCircle, suffix: '+' },
    { value: 100, label: 'Happy Clients', icon: Users, suffix: '+' },
    { value: 15, label: 'Awards Won', icon: Award, suffix: '+' },
  ];

  const skills = [
    { icon: Palette, title: 'Graphic Design', description: 'Creating purposeful visual identities, campaign materials, digital content, layouts, and communication assets' },
    { icon: Video, title: 'Videography', description: 'Capturing people, products, events, and stories through intentional visual direction and production.' },
    { icon: Video, title: 'Video Editing', description: 'Transforming raw footage into engaging stories through editing, pacing, sound, transitions, colour, and narrative structure.' },
    { icon: Layers, title: 'Motion Design', description: 'Bringing static ideas to life through animation, motion graphics, transitions, and dynamic visual storytelling.' },
    { icon: Camera, title: 'Photography', description: 'Creating expressive and purposeful imagery across portraits, events, products, lifestyle, and corporate content.' },
    { icon: Camera, title: 'Photo Editing', description: 'Refining images through retouching, colour correction, manipulation, compositing, and visual enhancement.' },
  ];

  const [statsRef, statsVisible] = useVisibility(0.2);

  return (
    <section className="min-h-screen py-24 bg-gradient-to-b from-black via-zinc-900 to-black pt-32 relative overflow-hidden">
      {/* ─── Background decorations ─── */}
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-yellow-600/20 via-transparent to-transparent pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-yellow-600/10 blur-3xl animate-pulse" />
      <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-yellow-600/10 blur-3xl animate-pulse delay-1000" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* ─── Page Hero ─── */}
        <PageHero
          badge="About Me"
          title="Design Meets <span class='text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-500'>Motion</span>"
          subtitle="I'm a multidisciplinary creative based in Los Angeles, blending graphic design and videography to craft compelling visual narratives."
        />

        {/* ─── Profile & Bio ─── */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="grid lg:grid-cols-2 gap-16 items-center mb-20"
        >
          <motion.div variants={fadeUp(0.1)} className="order-2 lg:order-1">
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-yellow-500/30 transition-all duration-300">
              {/* <div className="flex items-start gap-3 mb-6">
                <Quote size={24} className="text-yellow-400 flex-shrink-0 mt-1" />
                <p className="text-gray-300 text-lg leading-relaxed italic">
                  "I turn ideas into visual experiences."
                </p>
              </div> */}
              <p className="text-gray-400 leading-relaxed mb-4">
                I’m a multidisciplinary Creative Producer and Visual Communicator passionate about turning ideas into meaningful visual experiences
              </p>
              <p className="text-gray-400 leading-relaxed mb-4">
                My work spans graphic design, motion design, videography, video editing, photography, and photo editing, allowing me to approach projects from both a creative and production perspective.
              </p>
              <p className="text-gray-400 leading-relaxed mb-4">
                I enjoy working across the entire creative process—from understanding the brief and developing concepts to producing, editing, refining, and delivering the final work.
              </p>
              <p className="text-gray-400 leading-relaxed mb-4">
                For me, creativity isn’t simply about making things look good. It’s about understanding what needs to be communicated, who needs to receive it, and how visuals can make that message more powerful.
              </p>
              <div className="mt-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-500 to-yellow-600 flex items-center justify-center">
                  <User size={24} className="text-white" />
                </div>
                <div>
                  <div className="text-sm font-medium text-white">Xorlali</div>
                  <div className="text-xs text-gray-400">Creative Producer</div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div variants={fadeUp(0.2)} className="order-1 lg:order-2 flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-600/20 to-yellow-600/20 blur-2xl" />
              <img
                src={image}
                alt="Xorlali - Creative Producer"
                className="relative w-72 h-72 sm:w-80 sm:h-80 lg:w-96 lg:h-96 rounded-2xl object-cover border-2 border-yellow-500/20 shadow-2xl"
              />
              <div className="absolute -bottom-4 -right-4 bg-black/80 backdrop-blur-xl border border-white/10 rounded-2xl px-5 py-3 shadow-2xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <Award size={20} className="text-amber-400" />
                  </div>
                  {/* <div>
                    <div className="text-sm font-semibold text-white">5+ Years</div>
                    <div className="text-xs text-gray-400">Industry Experience</div>
                  </div> */}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* ─── Stats ─── */}
        <motion.div
          ref={statsRef}
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20"
        >
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={index}
                variants={fadeUp(0.1 * index)}
                className="text-center p-6 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/5 hover:border-yellow-500/30 transition-all duration-300 hover:bg-white/10 hover:-translate-y-1"
              >
                <div className="flex justify-center mb-3">
                  <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <Icon size={24} className="text-yellow-400" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-white">
                  {statsVisible ? (
                    <CountUp end={stat.value} duration={2.5} suffix={stat.suffix} />
                  ) : (
                    <span>0{stat.suffix}</span>
                  )}
                </div>
                <div className="text-sm text-gray-400 mt-1">{stat.label}</div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* ─── Skills ─── */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-yellow-500/10 border border-yellow-500/20 text-yellow-300 text-sm font-medium tracking-wider uppercase">
              {/* <Sparkles size={14} />  */}
              My Skills
            </span>
            <h3 className="font-serif text-3xl sm:text-4xl font-bold text-white mt-4">
              What I <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-500">Do Best</span>
            </h3>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {skills.map((skill, index) => {
              const Icon = skill.icon;
              return (
                <motion.div
                  key={index}
                  variants={fadeUp(0.1 * index)}
                  whileHover={{ y: -8, scale: 1.02 }}
                  className="group bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/5 hover:border-yellow-500/30 transition-all duration-300 hover:shadow-2xl hover:shadow-yellow-500/10"
                >
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-yellow-500/20 to-yellow-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon size={28} className="text-yellow-400" />
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-1">{skill.title}</h4>
                  <p className="text-sm text-gray-400">{skill.description}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;